/* G-String: Oneida size picker, bow finder tabs, G-String bow lookup, Oneida serial reader,
   and carrying bow details from "what to order" links into the product form. */
(() => {
  /* The customer's Oneida, remembered in their own browser only (Joey, Oct 25 2026: "unless a bow is selected already
     through the find my bow then it should auto fill"). Written when they pick a model on Find my bow (or a serial
     answer's model link), pick a size or add to cart on a model page, or pick in a "Pick your Oneida" box
     (assets/gs-choices.js); read to start those pickers on it, and they can always change it. localStorage "gs:bow":
     {"model": the Oneida model's handle, "size": the picked size's bow key ("phoenix-medium"), or "", "at": ms}.
     Since Oct 26 2026 (the string listing's brand pick) "model" can also be another brand's model ("dwai-kraken": a size
     or add to cart on its page) or one of our bows ("g-rex", or "g-rex-1285" once found on Find my bow, its page or the
     pick); the Oneida pickers only ever match their own models' handles.
     A key, never a length. No storage (a private window): nothing is remembered and every picker starts empty. */
  const BOW_STORE = 'gs:bow';
  const okModel = h => /^[a-z0-9-]{1,60}$/.test(String(h || ''));
  const bowGet = () => {
    try {
      const v = JSON.parse(localStorage.getItem(BOW_STORE) || 'null');
      if (!v || !okModel(v.model)) return null;
      const size = typeof v.size === 'string' && /^[a-z0-9-]{1,120}$/.test(v.size) && v.size.startsWith(v.model + '-') ? v.size : '';
      return { model: v.model, size, at: Number(v.at) || 0 };
    } catch (e) { return null; }
  };
  // size left out: keeps the size already remembered for this same model
  const bowSet = (model, size) => {
    if (!okModel(model)) return;
    const old = bowGet();
    let s = size === undefined ? (old && old.model === model ? old.size : '') : String(size || '');
    if (!s.startsWith(model + '-')) s = '';
    try { localStorage.setItem(BOW_STORE, JSON.stringify({ model, size: s, at: Date.now() })); } catch (e) { /* storage off: just don't remember */ }
  };
  window.GSBow = { get: bowGet, set: bowSet };
  // a model link on Find my bow or in a serial answer (and the GSB box's string link: data-gs-remember="gsb")
  document.addEventListener('click', e => {
    const a = e.target.closest('a[data-gs-oneida], a[data-gs-remember]');
    if (a) bowSet(a.dataset.gsOneida || a.dataset.gsRemember);
  });

  /* Oneida serial numbers, read ONLY as far as Oneida's own records go (the serial number history and its chart,
     and the dealer manual). The rule from the shop: never state a guess as fact. Where the records don't settle
     the model or the year, the answer says so and asks for a photo.
     - 1982–85: numbers only (no model). June 1986 – March 1987: A + numbers (no model). 2001–09: C.P.A. and a number
       (no model; the year as far as Oneida's chart places the number, see CPA below). C.P. Oneida also engraved "A" and
       the C.P.A. number on some risers, so an A + numbers answer gives that reading too, under its own label (A_CPA).
     - About 1987–2000: model letter(s), then (A, K, P, S, T) a finish letter, E = not camo, C = camo, then from 1988
       a year letter. H5 (H500) and H2 (H250) have no finish letter. Printed examples: AEJ1111, PCI1111, H5D1111.
     Anything that isn't Oneida's chart (our shop's look-alike tip, what collectors say) goes under its own label in
     the answer's notes, never in the chart's answer itself. */
  const SERIAL = {
    yearLetters: { B: 1988, D: 1989, F: 1990, G: 1991, I: 1992, J: 1993, K: 1994, L: 1995, M: 1996, N: 1997, O: 1998, P: 1999, Q: 2000 },
    // the years Oneida's serial chart gives each model a code can mean (the chart, not the model pages' wider spans)
    models: {
      h500: ['H500', 1982, 1989], h250: ['H250', 1984, 1989], screaming: ['Screaming Eagle', 1987, 1989], strike: ['Strike Eagle', 1990, 1992],
      phantom: ['Phantom', 1990, 1992], phantom2: ['Phantom II', 1992, 1996], aeroforce: ['AeroForce', 1992, 2000],
      tomcat: ['Tomcat', 1990, 1992], tomcat2: ['Tomcat II', 1992, 1996], tomcat3: ['Tomcat III (T-III)', 1997, 1997],
      // Lite Force: Oneida's serial chart has 1997 only; Joey's Lite Force chart (found Oct 19 2026) says made 1997–1999,
      // serials starting with LM, and he asked for all three years
      liteforce: ['Lite Force / LF Magnum', 1997, 1999], stealth: ['Stealth Eagle', 1998, 2000], blackeagle00: ['Black Eagle (2000)', 2000, 2000],
      // C.P. Oneida (C.P.A. serials): names for the model pages the C.P.A. chart's rows point to
      blackeagle0103: ['Black Eagle (2001–03)', 2001, 2003], proeagle: ['Pro Eagle', 2001, 2007], be2: ['Black Eagle ESC / Black Eagle II', 2002, 2009],
      talon: ['Talon', 2004, 2009], hawk: ['Hawk', 2003, 2009], osprey_cp: ['Osprey (before 2017)', 2004, 2009], extreme: ['Extreme Eagle', 2006, 2007],
      falcon: ['Falcon', 2009, 2009],
      // the newer (2016-on) labels (newer() below): named for links only, never read from a chart code
      osprey_new: ['Osprey (2017–today)', 2017, 2017], phoenix: ['Phoenix', 2017, 2017],
    },
    // code -> the models it can be (the chart gives the code; the year picks among them, never a guess)
    codes: { A: ['aeroforce'], K: ['strike'], P: ['phantom', 'phantom2'], T: ['tomcat', 'tomcat2', 'tomcat3'], S: ['screaming'], H5: ['h500'], H2: ['h250'], LM: ['liteforce'] },
    // not on Oneida's serial chart, so never read from a code: named only for the hand-lettered "Tx" labels (below)
    unlisted: { tomcatexp: 'Tomcat EXP', tomcat3exp: 'T-III EXP' },
  };
  // (decode() runs as soon as the page loads with ?serial=, so these come first)
  const PHOTO = ' Text us a photo of the number at 270-318-2020 and we\'ll look it up by hand.';
  const PHOTO_LABEL = ' Text us a photo of the label at 270-318-2020 and we\'ll look it up by hand.';
  const yearOf = c => SERIAL.yearLetters[c];
  const inYear = (id, y) => { const m = SERIAL.models[id]; return m && y >= m[1] && y <= m[2]; };
  const modelName = id => SERIAL.models[id]?.[0] || SERIAL.unlisted[id] || '';
  const names = ids => { const n = ids.map(modelName); return n.length > 1 ? n.slice(0, -1).join(', ') + ' or ' + n[n.length - 1] : n[0]; };
  /* Notes under an answer that aren't Oneida's chart, each with its own label (Joey, Oct 25 2026: "sure" to collector
     info "From collectors, not Oneida's papers"; never stated as Oneida fact).
     - The H500 / Screaming Eagle / H250 look-alike tip from our shop (Joey, Oct 22 and Oct 25 2026, #28–29): on every
       answer that can be one of them, and on "A" + numbers (June 1986 – March 1987).
     - Labels with a typed Q or R and a number, and the model letters written in by hand (LM, AC, Tx): collectors date them
       to about 1997, while Oneida's year table says Q = 2000 and has no R (Joey, Oct 25 2026, #51). */
  const LOOKALIKE = { label: 'From our shop', text: 'The H500, Screaming Eagle and H250 look alike. H500: nylon cams and no plastic limb tips. Screaming Eagle: looks the same, but with aluminum (alloy) cams. H250: nylon cams too, with plastic tips on its outer limbs.' };
  // the cams confirmed by Joey, Oct 26 2026: "THE H250 IS CONFIRMED TO HAVE NYLON CAMS. H500 NYLON CAMS SCREAMIN EAGLE ALUMINUM ALLOY CAMS."
  const LOOKALIKE_IDS = ['h500', 'h250', 'screaming'];
  const HANDWRITTEN = { label: 'From collectors, not Oneida\'s papers', text: 'Collectors think some labels like this (a typed letter and number, with the model letters written in by hand) are from about 1997.' };
  // Joey's Black Eagle label: "BE" written in blue next to a typed Q number (Oct 26 2026, #225), and Oneida Labs' website
  // offered the Black Eagle from January 1999, so these labels aren't all from about 1997 (#229 f: "i agree with all")
  const HAND_BE = { label: 'From our shop and Oneida Labs\' website (1999)', text: 'A Black Eagle in our shop has the same kind of label ("BE" written in next to a typed Q number), and Oneida Labs offered the Black Eagle from January 1999, so not every label like this is from about 1997.' };
  // A typed Q number with no letters. Oct 28 2026 (#268) it read "Most likely a Stealth Eagle"; then Joey's Lite Force label
  // (Oct 29: typed Q7528 with a tiny hand-written "LM", inside the Stealth range Q7111-Q9556, the same 43 1/4" string ticked):
  // "a typed q should probably have them questions about the bow to figure out if its a stealth or a liteforce. for example
  // cams or pylons or anything else that would be a tell". Labels seen: Stealths Q7111, Q9528, Q9556, SE Q7653, SE Q8119;
  // Lite Forces LM Q7528, LM Q9164; one Black Eagle, BE Q10769 (five digits). The tells, from Oneida's papers (research,
  // Oct 29): the Lite Force is a pylon bow with one K-Cam at each end and the power cable's loop pinned into a clevis (the
  // T-III / AeroForce / Lite-Force manual); the Stealth's cam is two plates joined through the riser, and its power cable
  // runs "over the saddle, into the cable slot" (Stealth manual; 2.0 bulletin: "The saddle is designed to capture both yoke
  // and power cable"). The Lite Force has a saddle too (its manual's Fig. 1), so the question asks where the power cable
  // ends. Oneida Labs' 1999-2000 pages list the Stealth Eagle 2.0 only in 35/55 and 50/70, the Lite Force in all four. Both
  // take a 43 1/4" string; a "Stealth Long" limb exists with no string on file, so the string boxes aren't a tell. Five
  // digits ask only the cam question: the Black Eagle's cam is a split pair too (Joey, Oct 19), but where its power cable
  // ends isn't in its papers.
  const Q_LABELS = { label: 'From our shop', text: 'Stealth Eagle and Lite Force labels we\'ve seen both have a typed Q and four digits, in the same range (Q7111 to Q9556). Some have the model letters written in by hand next to the Q, sometimes small and easy to miss: "SE" for the Stealth Eagle, "LM" for the Lite Force. If yours does, type them in front, like SE Q1234 or LM Q1234. A Black Eagle in our shop has a Q label too (Q10769, with "BE" written in).' };
  const Q_TICKS = { label: 'From Oneida Labs\' website (1999–2000)', text: 'Oneida Labs listed the Stealth Eagle 2.0 only in 35/55 and 50/70, and the Lite Force in 25/45, 35/55, 50/70 and 60/80. So 25/45 or 60/80 ticked on your label points to the Lite Force. The string boxes don\'t settle it: 43 1/4″ fits both bows.' };
  // Questions about the bow itself (askBox under the answer): each answer names the model ids it points to
  const ASK_CAM = { q: 'Look at the top end of the riser (the metal middle of the bow), where the cables wrap around the cam. Is the cam tucked inside the riser, between two arms of riser metal? Or is the riser in the middle, with a cam piece on each outside face of it, joined by an axle through the riser?',
    a: [['Cam between two riser arms', ['liteforce']], ['Cam pieces outside the riser', ['stealth']]] };
  const ASK_CABLE = { q: 'Follow the power cable (the cable closest to the string) to the hinge, where the short limb meets the curved outer limb. Does the cable end there, looped around a pin in a small U-shaped bracket? Or does it run on past the hinge, over the block across the outer limb (the saddle), into a slot under the saddle\'s screw? The Lite Force has a saddle too, so look at where the power cable itself ends.',
    a: [['Loop on a pin at the hinge', ['liteforce']], ['Runs on under the saddle', ['stealth']]] };
  const ASK_Q = five => ({
    intro: 'Not sure which? Look at your bow:',
    // one model: a longer pylon bow with this label could be a T-III (49") or AeroForce (48"); the Lite Force is 44"
    // (Oneida Labs' site, Feb 1999), and both have its cam and bracket
    after: { liteforce: ' The Lite Force is about 44″ tip to tip. If yours is longer, text us a photo.' },
    qs: five ? [{ ...ASK_CAM, a: [ASK_CAM.a[0], [ASK_CAM.a[1][0], ['stealth', 'blackeagle00']]] }] : [ASK_CAM, ASK_CABLE],
  });
  // (e) Oneida Labs' own website, Jan and Sept 1999 (Wayback 19990125091248, 19990904002431; research, Oct 26 2026)
  const BE_1999 = { label: 'From Oneida Labs\' website (1999)', text: 'Oneida Labs offered the Black Eagle from January 1999 as a "pre-production limited edition", with dealers getting it after March 1999. So a Black Eagle can be a 1999 bow, even though the serial chart lists it for 2000.' };
  /* The newer (2016-on) Oneida labels, read by newer() (#229, Joey, Oct 26 2026: "i agree with all"):
     - (c) the label's Model line: OSR-S/M/L (Osprey) and PHX-S/M/L (Phoenix) are the Short, Medium and Long in the
       current company's manual. OSR alone doesn't tell an older Osprey from a newer one (OSR-SLF and OSR-MLF go with
       older 43" strings), so the string length and size decide; SLF and MLF are older (before 2017) size names.
     - (b) the serial: on the newer Ospreys in Joey's shop it starts with O and the size letter (OM… with OSR-M, OL…
       with OSR-L; three labels, Oct 26 2026). What the numbers after mean isn't known, so no year is read.
     - (a) P + a size letter or a digit (like PM1234 or P123456) isn't a Phantom (P, then E or C, then a year letter, like
       PCI1111). Newer Phoenix numbers may look like that, but nothing from Oneida says so, so no model is named. */
  const SIZE = { S: 'Short', M: 'Medium', L: 'Long' };
  const NEWER_STRINGS = 'the newer (2017 on) Short, Medium and Long take 41¾″, 42¼″ and 43¾″ strings';
  const PHOENIX_STRINGS = 'The Phoenix came out in 2017. Its Short, Medium and Long take 41¾″, 42¼″ and 43¾″ strings.';
  const OLDER_OSPREY = 'Older Ospreys (before 2017) take 43″ (Super Short, SLF), 43¾″ (Short), 43″ (Medium L.F.) or 44½″ (Medium).';
  const SHOP_O = { label: 'From our shop', text: 'On the newer Osprey labels in our shop the serial starts with O and the size letter, and the label\'s Model line matches it: OM… with OSR-M (Medium), OL… with OSR-L (Long). We don\'t know what the numbers after the letters mean, so we don\'t read a year from them.' };
  const slug = s => String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  /* C.P. Oneida (2001–2009) serials: C.P.A. and a number. Oneida's serial chart (and its serial number history page) gives
     one number per year, two in 2004 and 2005, and a span in 2006. It never says how many digits a serial has or whether
     its number is the first of the year, so the reader only says what the chart says and where a number sits on it:
     - 2001–2004 are printed as one digit (C.P.A. 0 … 4): a number starting with that digit is on that row.
     - 2005–2009 are printed as four digits (3500, 4500, 4600–6000, 6500, 7500, 8000): a four-digit number on one is that
       year; between two, either year; past 8000, 2009 or later (the chart stops at 2009).
     A number can be on both parts (four digits starting with 3 or 4; one just below 3500 counts too, since the chart
     doesn't say where 2005's numbers start): then it's any of those years.
     The model isn't in the number: the answer offers the models the chart lists for those years. */
  const CPA = [
    // [chart number, last number of a span, year, models as the chart prints them, the same spelled out, model pages].
    // The 2005 "BE" gets no page of its own: our Black Eagle (2001–03) page stops at 2003, and the chart doesn't say
    // which Black Eagle it is (BE II, on the same row, has its page).
    ['0', '', 2001, 'Black Eagle & Pro', 'Black Eagle and Pro Eagle', ['blackeagle0103', 'proeagle']],
    ['1', '', 2002, 'Black Eagle, Pro, Esc', 'Black Eagle, Pro Eagle and Black Eagle ESC', ['blackeagle0103', 'proeagle', 'be2']],
    ['2', '', 2003, 'BE, Pro, ESC, & Hawk', 'Black Eagle, Pro Eagle, Black Eagle ESC and Hawk', ['blackeagle0103', 'proeagle', 'be2', 'hawk']],
    ['3', '', 2004, 'ESC, Pro, Talon', 'Black Eagle ESC, Pro Eagle and Talon', ['be2', 'proeagle', 'talon']],
    ['4', '', 2004, 'Hawk, Osprey', 'Hawk and Osprey', ['hawk', 'osprey_cp']],
    ['3500', '', 2005, 'BE, BE II, Pro, Talon, Hawk', 'Black Eagle, Black Eagle II, Pro Eagle, Talon and Hawk', ['be2', 'proeagle', 'talon', 'hawk']],
    ['4500', '', 2005, 'Osprey', 'Osprey', ['osprey_cp']],
    ['4600', '6000', 2006, 'BE II, Pro, Talon, Hawk, Osprey, Extreme', 'Black Eagle II, Pro Eagle, Talon, Hawk, Osprey and Extreme Eagle', ['be2', 'proeagle', 'talon', 'hawk', 'osprey_cp', 'extreme']],
    ['6500', '', 2007, 'BE II, Pro, Talon, Hawk, Osprey, Extreme', 'Black Eagle II, Pro Eagle, Talon, Hawk, Osprey and Extreme Eagle', ['be2', 'proeagle', 'talon', 'hawk', 'osprey_cp', 'extreme']],
    ['7500', '', 2008, 'BE II, Talon, Hawk, Osprey', 'Black Eagle II, Talon, Hawk and Osprey', ['be2', 'talon', 'hawk', 'osprey_cp']],
    ['8000', '', 2009, 'BEII, Talon, Hawk, Osprey, Falcon', 'Black Eagle II, Talon, Hawk, Osprey and Falcon', ['be2', 'talon', 'hawk', 'osprey_cp', 'falcon']],
  ];
  const cpaLabel = row => 'C.P.A. ' + row[0] + (row[1] ? '–' + row[1] : '');
  const orList = a => a.length > 1 ? a.slice(0, -1).join(', ') + ' or ' + a[a.length - 1] : String(a[0]);
  // -> { rows: [chart rows it sits on], later: true when past the chart's last number, said: [sentences] }
  function cpaPlace(digits) {
    const rows = [], said = [];
    let later = false;
    const early = CPA.find(r => r[0].length === 1 && r[0] === digits[0]);
    if (early) {
      rows.push(early);
      said.push('Your number starts with ' + digits[0] + ', and Oneida\'s chart lists ' + cpaLabel(early) + ' for ' + early[2] + ' (' + early[4] + ').');
    }
    const four = CPA.filter(r => r[0].length === 4);
    if (digits.length === 4) {
      const n = Number(digits);
      const on = four.find(r => n >= Number(r[0]) && n <= Number(r[1] || r[0]));
      if (on) {
        rows.push(on);
        said.push('Oneida\'s chart lists ' + (on[1] ? cpaLabel(on) : 'C.P.A. ' + digits) + ' for ' + on[2] + ' (' + on[4] + ').');
      } else if (n > Number(four[four.length - 1][0])) {
        const last = four[four.length - 1];
        rows.push(last); later = true;
        said.push('It\'s past ' + cpaLabel(last) + ', the last number on Oneida\'s chart, listed for ' + last[2] + ' (' + last[4] + '). The chart stops there, and we don\'t have records for later C.P.A. numbers yet.');
      } else if (n > Number(four[0][0])) {
        const i = four.findIndex(r => n < Number(r[0]));
        const a = four[i - 1], b = four[i];
        rows.push(a, b);
        said.push('It falls between ' + cpaLabel(a) + ', which Oneida\'s chart lists for ' + a[2] + ' (' + a[4] + '), and ' + cpaLabel(b) + ', listed for ' + b[2] + ' (' + b[4] + ').');
      } else if (digits[0] === four[0][0][0]) {
        // starts with 3 but is below 3500: the chart doesn't say 3500 was 2005's first number, so 2005 stays possible
        rows.push(four[0]);
        said.push('It\'s just below ' + cpaLabel(four[0]) + ', which Oneida\'s chart lists for ' + four[0][2] + ' (' + four[0][4] + '). The chart doesn\'t say where 2005\'s numbers start.');
      }
    } else if (digits.length > 4 || !early) {
      said.push('Oneida\'s chart prints the 2005–2009 numbers with four digits (like C.P.A. 3500), so we can\'t place a number with ' + digits.length + ' digits on that part of the chart.');
    }
    return { rows, later, said };
  }
  // Oneida's C.P.A. chart as a small table, the rows a number sits on marked
  function cpaChart(on) {
    const box = document.createElement('details');
    box.className = 'gs-cpa';
    box.open = true;
    box.innerHTML = '<summary>Oneida\'s C.P.A. serial chart</summary><table><thead><tr><th>Year</th><th>Models (as printed)</th><th>Serial</th></tr></thead><tbody></tbody></table>';
    CPA.forEach((row, i) => {
      const tr = document.createElement('tr');
      if (on.includes(i)) tr.className = 'is-on';
      [String(row[2]), row[3], cpaLabel(row)].forEach(t => { const td = document.createElement('td'); td.textContent = t; tr.append(td); });
      box.querySelector('tbody').append(tr);
    });
    return box;
  }
  function decodeCPA(s) {
    const digits = s.slice(3);
    const base = 'Serials starting with C.P.A. are from C.P. Oneida in Michigan (2001 to 2009 on Oneida\'s chart).';
    if (!digits) return { title: 'C.P. Oneida Eagle Bows, 2001–2009', year: '', conf: 'years', links: [], cpa: [],
      why: base + ' They don\'t show the model. Type the numbers after C.P.A. too, and we\'ll show where they sit on Oneida\'s chart.' + PHOTO };
    const p = cpaPlace(digits);
    const years = [...new Set(p.rows.map(r => r[2]))].sort();
    const links = [...new Set(p.rows.flatMap(r => r[5]))];
    // a number longer than the chart's can't be placed on its 2005–2009 part, so no year is stated (the why says what fits)
    const year = !years.length || digits.length > 4 ? '' : orList(years) + (p.later ? ' or later' : '');
    return { title: 'C.P. Oneida Eagle Bows', year, conf: year && years.length === 1 && !p.later ? 'family' : 'years', links, cpa: p.rows.map(r => CPA.indexOf(r)),
      why: base + ' ' + p.said.join(' ') + (links.length ? ' The number doesn\'t show the model, so check the label on your bow.' : ' C.P.A. numbers don\'t show the model.') + PHOTO };
  }

  /* ---------- Share / Copy link: answers people can send each other (e.g. in a Facebook group) ----------
     Any [data-gs-share] button shares data-gs-share-url (or this page). Phones get the share sheet; computers copy
     the link. The message goes to the [data-gs-share-msg] next to the button. */
  document.addEventListener('click', async e => {
    const b = e.target.closest('[data-gs-share]');
    if (!b) return;
    const url = new URL(b.dataset.gsShareUrl || location.href, location.origin).href;
    const title = b.dataset.gsShareTitle || document.title;
    const msg = b.parentElement.querySelector('[data-gs-share-msg]');
    const say = t => { if (msg) msg.textContent = t; };
    if (navigator.share && window.matchMedia('(pointer: coarse)').matches) {
      try { await navigator.share({ title, url }); return; } catch (err) { if (err && err.name === 'AbortError') return; }
    }
    try { await navigator.clipboard.writeText(url); say('Link copied. Paste it anywhere, like a Facebook post.'); }
    catch (err) { say('Copy this link: ' + url); }
  });
  const shareButton = (url, title, label = 'Share this answer') => {
    const wrap = document.createElement('p');
    wrap.className = 'gs-share';
    wrap.innerHTML = '<button type="button" class="gs-btn gs-btn--ghost gs-share__btn" data-gs-share></button><span class="gs-hint gs-share__msg" data-gs-share-msg role="status"></span>';
    const b = wrap.querySelector('button');
    b.textContent = label;
    b.dataset.gsShareUrl = url;
    b.dataset.gsShareTitle = title;
    return wrap;
  };
  const narrow = () => window.matchMedia('(max-width: 860px)').matches;
  const smooth = () => (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');

  /* ---------- Oneida model page: pick a size ---------- */
  document.querySelectorAll('[data-gs-model]').forEach(root => {
    const status = root.querySelector('[data-gs-status]');
    const handle = root.dataset.gsModelHandle || '';
    // remember: only a size the customer clicks (a shared #size- link or the remembered size itself isn't a new pick)
    const pick = (i, scroll, remember) => {
      root.querySelectorAll('[data-gs-panel]').forEach(p => { p.hidden = p.dataset.gsPanel !== String(i); });
      root.querySelectorAll('button[data-gs-pick]').forEach(c => c.setAttribute('aria-pressed', String(c.dataset.gsPick === String(i))));
      root.querySelectorAll('tr[data-size]').forEach(r => r.classList.toggle('is-picked', r.dataset.size === String(i)));
      const panel = root.querySelector('[data-gs-panel="' + i + '"]');
      if (status && panel) status.textContent = panel.dataset.gsSummary || '';
      if (panel) {
        const ctx = { bow: panel.dataset.gsBow, string_length: panel.dataset.gsString, bow_size: panel.dataset.gsSize, spec: panel.dataset.gsSpec };
        if ('gsNospecString' in panel.dataset) ctx.nospec_string = panel.dataset.gsNospecString === 'true';
        if ('gsNospecCables' in panel.dataset) ctx.nospec_cables = panel.dataset.gsNospecCables === 'true';
        setBowContext(root, ctx);
        if (remember && handle) bowSet(handle, panel.dataset.gsSpec || '');
      }
      history.replaceState(null, '', '#size-' + i);
      if (scroll && narrow() && panel) panel.scrollIntoView({ behavior: smooth(), block: 'start' });
    };
    root.addEventListener('click', e => {
      const t = e.target.closest('[data-gs-pick]');
      if (t) pick(t.dataset.gsPick, true, true);
      const tg = e.target.closest('[data-gs-toggle]');
      if (tg) {
        const el = document.getElementById(tg.dataset.gsToggle);
        el.hidden = !el.hidden;
        tg.setAttribute('aria-expanded', String(!el.hidden));
      }
    });
    const m = location.hash.match(/^#size-(\d+)$/);
    if (m && root.querySelector('[data-gs-panel="' + m[1] + '"]')) pick(m[1], false, false);
    else {
      // back on this bow's page: the size this customer picked before (here or in a picker) starts picked
      const r = bowGet();
      const was = r && r.model === handle && r.size && [...root.querySelectorAll('[data-gs-panel]')].find(p => p.dataset.gsSpec === r.size);
      if (was) pick(was.dataset.gsPanel, false, false);
    }
  });

  /* ---------- Bow finder ---------- */
  document.querySelectorAll('[data-gs-finder]').forEach(root => {
    const tabs = [...root.querySelectorAll('[role="tab"]')];
    const show = (id, focusTab) => {
      tabs.forEach(t => {
        const on = t.dataset.gsTab === id;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        if (on && focusTab) t.focus();
      });
      root.querySelectorAll('[data-gs-tabpanel]').forEach(p => { p.hidden = p.dataset.gsTabpanel !== id; });
    };
    root.addEventListener('click', e => {
      const t = e.target.closest('[data-gs-tab]');
      if (t) { show(t.dataset.gsTab); history.replaceState(null, '', '#' + t.dataset.gsTab); }
      const g = e.target.closest('[data-gs-goto]');
      if (g) {
        show(g.dataset.gsGoto);
        history.replaceState(null, '', '#' + g.dataset.gsGoto);
        const field = root.querySelector('[data-gs-tabpanel="' + g.dataset.gsGoto + '"] input');
        field?.focus();
        field?.scrollIntoView({ behavior: smooth(), block: 'center' });
      }
    });
    root.querySelector('[role="tablist"]')?.addEventListener('keydown', e => {
      const i = tabs.indexOf(document.activeElement);
      if (i < 0) return;
      const next = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: tabs.length - 1 }[e.key];
      if (next === undefined) return;
      e.preventDefault();
      const t = tabs[(next + tabs.length) % tabs.length];
      show(t.dataset.gsTab, true);
    });
    const h = location.hash.slice(1);
    show(['oneida', 'gstring', 'serial'].includes(h) ? h : 'oneida');

    // Oneida model search: every word typed has to match a model, in any order, so "oneida osprey" and "black eagle 2000"
    // work. "oneida", "bow" and the like are skipped; "h500" finds "h-500"; "tomcat-2" reads as "tomcat 2"; a word of one or
    // two letters ("i", "ii", "2") has to be a whole word; a year ("osprey 2016") matches the years the model was built.
    // Oneida's target versions aren't on the site (we have no string chart for them), so a search for one says so.
    const filter = root.querySelector('[data-gs-filter]');
    const nomatch = root.querySelector('[data-gs-nomatch]');
    const SKIP = new Set(['oneida', 'bow', 'bows', 'compound', 'the', 'my']);
    const flat = t => t.replace(/[-–]/g, '');
    const thisYear = new Date().getFullYear();
    const yearsOf = a => {
      const t = (a.querySelector('span')?.textContent || '').toLowerCase();
      const ys = (t.match(/\b(19|20)\d\d\b/g) || []).map(Number);
      if (!ys.length) return null;
      return [ys[0], t.includes('today') ? thisYear : ys[ys.length - 1]];
    };
    const wordHit = (w, hay, words, span) => {
      if (/^(19|20)\d\d$/.test(w) && span) return +w >= span[0] && +w <= span[1];
      if (w.length <= 2) return words.has(w) || words.has(flat(w));
      if (hay.includes(w) || hay.includes(flat(w))) return true;
      const parts = w.split(/[-–]/).filter(Boolean);
      return parts.length > 1 && parts.every(p => wordHit(p, hay, words, span));
    };
    // X80 (Oneida's 80% let-off versions, not a bow of its own: Joey, Oct 27 2026, #155) gets its own note whenever it's typed
    const x80 = root.querySelector('[data-gs-x80]');
    filter?.addEventListener('input', () => {
      const typed = filter.value.toLowerCase().replace(/\bx[\s\-–]?80\b/g, 'x80').replace(/[^a-z0-9.\-–\s]/g, ' ').split(/\s+/).filter(w => w && !SKIP.has(w));
      if (x80) x80.hidden = !typed.some(w => w.split(/[-–]/).includes('x80'));
      // "h 500" and "se 600" mean "h500" and "se600": also try a short letter code joined to the number after it
      const joined = typed.reduce((out, w, i) => (i && /^[a-z]{1,3}$/.test(typed[i - 1]) && /^\d/.test(w) ? out.splice(-1, 1, typed[i - 1] + w) : out.push(w), out), []);
      const variants = joined.join(' ') === typed.join(' ') ? [typed] : [typed, joined];
      let any = false;
      root.querySelectorAll('[data-gs-era]').forEach(era => {
        let n = 0;
        era.querySelectorAll('.gs-model').forEach(a => {
          const names = a.dataset.names, hay = names + ' ' + flat(names);
          const words = new Set(hay.split(/[\s(),/]+/).filter(Boolean));
          const span = yearsOf(a);
          const hit = !typed.length || variants.some(v => v.every(w => wordHit(w, hay, words, span)));
          a.hidden = !hit; if (hit) n++;
        });
        era.hidden = n === 0; if (n) any = true;
      });
      if (!nomatch) return;
      nomatch.dataset.text ??= nomatch.textContent;
      // Target versions take the regular model's specs (Joey, Oct 25 2026, #38); their names are search names on those pages
      nomatch.textContent = !any && typed.includes('target')
        ? 'A Target version takes the same strings and cables as the regular model: search for Phantom, Strike Eagle or AeroForce. Not sure? Call or text us at 270-318-2020.'
        : nomatch.dataset.text;
      nomatch.hidden = any;
    });

    // G-String bow lookup: each numbered bow has its own page at /pages/bow/<model>-<number>
    const form = root.querySelector('[data-gs-lookup]');
    if (form) {
      const out = root.querySelector('[data-gs-lookup-result]');
      const start = [...out.childNodes].map(n => n.cloneNode(true)); // the "Enter your number" box, to go back to
      // the shop's numbers: 1285, or letters then digits like JC067 and W012 (leading zeros only matter after letters)
      const numOf = typed => {
        const raw = String(typed || '').trim().toUpperCase().replace(/^#/, '').replace(/\s+/g, '')
          .replace(/^(JC|W)([0-9O]+)$/, (_, p, n) => p + n.replace(/O/g, '0')); // JCO67 typed for JC067
        return /^\d+$/.test(raw) ? String(parseInt(raw, 10)) : raw;
      };
      // GSB records aren't online yet (Joey, Oct 25 2026, #150: "show we are still adding gsb serials"), so picking GSB
      // says so at once, with ways to still order (template data-gs-gsb), instead of a lookup that can't find anything
      const gsbTpl = root.querySelector('template[data-gs-gsb]');
      const showGsb = num => {
        if (!gsbTpl) return false;
        const box = gsbTpl.content.cloneNode(true);
        box.querySelector('[data-gs-nf-title]').textContent = 'GSB' + (num ? ' #' + num : '');
        out.replaceChildren(box);
        out.dataset.gsGsb = '1';
        return true;
      };
      let model = form.querySelector('[data-gs-bowmodel][aria-pressed="true"]')?.dataset.gsBowmodel || '';
      const preset = new URLSearchParams(location.search).get('bow');
      const pb = preset && form.querySelector('[data-gs-bowmodel="' + CSS.escape(preset) + '"]');
      if (pb) { model = preset; pb.setAttribute('aria-pressed', 'true'); }
      if (model === 'GSB') showGsb(numOf(form.number.value));
      // ?bow=Gnat&number=930: the shipping email's button for a used in-stock bow (Joey, Oct 25 2026, #160), whose record
      // may not be online, so it looks the number up here: the bow's page if it is, else "We don't have this record yet"
      const presetNum = numOf(new URLSearchParams(location.search).get('number'));
      if (pb && presetNum && model !== 'GSB') {
        form.number.value = presetNum;
        setTimeout(() => form.dispatchEvent(new Event('submit', { cancelable: true })), 0); // after the submit handler below is on
      }
      form.addEventListener('click', e => {
        const b = e.target.closest('[data-gs-bowmodel]');
        if (!b) return;
        model = b.dataset.gsBowmodel;
        form.querySelectorAll('[data-gs-bowmodel]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
        if (model === 'GSB') showGsb(numOf(form.number.value));
        else if (out.dataset.gsGsb) { out.replaceChildren(...start.map(n => n.cloneNode(true))); delete out.dataset.gsGsb; }
      });
      form.addEventListener('submit', async e => {
        e.preventDefault();
        if (!model) {
          const p = document.createElement('p');
          p.className = 'gs-callout';
          p.textContent = 'Pick G-Rex, Gnat, Pro2G or GSB first: the same number means a different bow on each. Not sure which you have? Text us a photo at 270-318-2020.';
          out.replaceChildren(p);
          delete out.dataset.gsGsb;
          form.querySelector('[data-gs-bowmodel]')?.focus();
          return;
        }
        const num = numOf(form.number.value);
        // GSB: nothing to look up yet, with or without a number
        if (model === 'GSB' && showGsb(num)) return;
        if (!num) { form.number.focus(); return; }
        const url = '/pages/bow/' + slug(model) + '-' + slug(num);
        if (model !== 'GSB') {
          try {
            const res = await fetch(url, { method: 'HEAD' });
            if (res.ok) { bowSet(slug(model) + '-' + slug(num)); window.location.href = url; return; } // their bow: remembered for the string listing's brand pick
          } catch (err) { /* fall through to the not-found message */ }
        }
        delete out.dataset.gsGsb;
        const tpl = root.querySelector('template[data-gs-notfound]').content.cloneNode(true);
        tpl.querySelector('[data-gs-nf-title]').textContent = model + ' #' + num;
        // G-Rex and Gnat numbers overlap: say so if the other model has this number on file
        const other = { 'G-Rex': 'Gnat', Gnat: 'G-Rex' }[model];
        if (other) {
          const ourl = '/pages/bow/' + slug(other) + '-' + slug(num);
          try {
            const r2 = await fetch(ourl, { method: 'HEAD' });
            if (r2.ok) {
              const p = document.createElement('p');
              p.innerHTML = 'We do have a <a class="gs-link"></a> on file. G-Rex and Gnat numbers overlap. Is that your bow?';
              const a = p.querySelector('a'); a.href = ourl; a.textContent = other + ' #' + num;
              tpl.querySelector('h3').after(p);
            }
          } catch (err) { /* no hint */ }
        }
        out.replaceChildren(tpl);
      });
    }

    // Oneida serial reader
    const sform = root.querySelector('[data-gs-serial]');
    const sout = root.querySelector('[data-gs-serial-result]');
    const contact = root.querySelector('template[data-gs-contact]');
    /* "Know your bow? Tell us" (Joey, Oct 25 2026, #148: "sure"), under every answer Oneida's chart doesn't fully settle:
       not in our records, check the number, model only, years only, and year only (more than one model that year). The
       customer picks the model (or "Not sure"), can add the size and string length from the label, and gives an email
       (Shopify's contact form needs one). It's an ordinary post to the store's own contact form (template data-gs-tell,
       Shopify may show its spam check page first), so Joey gets a normal contact email; its message is written here from
       the serial, the reader's answer and the picks. Nothing is kept on the site. Never a cable length. */
    const tell = root.querySelector('template[data-gs-tell]');
    const TELL = ['none', 'check', 'model', 'years', 'family'];
    // Oneida's printed examples (the "Try:" buttons, e.g. PCI1111) aren't anyone's bow: no "Tell us" under them
    const flatSerial = s => String(s || '').toUpperCase().replace(/[\s\-–—]+/g, '');
    const EXAMPLES = new Set([...root.querySelectorAll('[data-gs-serial-ex]')].map(b => flatSerial(b.dataset.gsSerialEx)));
    const tellBox = (serial, said) => {
      const frag = tell.content.cloneNode(true);
      const f = frag.querySelector('form');
      const body = f?.querySelector('[data-gs-tell-body]');
      if (!body) return frag;
      const val = sel => (f.querySelector(sel)?.value || '').trim();
      const compose = () => {
        body.value = [
          'Oneida serial number from the serial reader on Find my bow.',
          'Serial number: ' + serial,
          'The serial reader said: ' + said,
          'Model (the customer\'s pick): ' + (val('[data-gs-tell-model]') || 'not picked'),
          val('[data-gs-tell-size]') && 'Size on the label: ' + val('[data-gs-tell-size]'),
          val('[data-gs-tell-string]') && 'String length on the label: ' + val('[data-gs-tell-string]'),
        ].filter(Boolean).join('\n');
      };
      f.addEventListener('input', compose);
      f.addEventListener('change', compose);
      // the browser checks the model and the email first; then the page goes to Shopify, so the button says it's on its way
      f.addEventListener('submit', () => {
        compose();
        const b = f.querySelector('[data-gs-tell-send]');
        if (b) { b.dataset.gsLabel ??= b.textContent; b.textContent = 'Sending…'; }
      });
      compose();
      return frag;
    };
    // back with the Back button after sending: the button reads as it did
    window.addEventListener('pageshow', () => root.querySelectorAll('[data-gs-tell-send][data-gs-label]').forEach(b => { b.textContent = b.dataset.gsLabel; }));
    /* Questions about the bow itself, under an answer that names more than one model (a typed Q: Stealth Eagle or Lite
       Force; Joey, Oct 29 2026). Each pick points to models; when the picks agree the answer says which and that model's
       button becomes the main one, and when they don't it asks for a photo. */
    let askIds = 0;
    const askBox = (ask, div) => {
      const box = document.createElement('div');
      box.className = 'gs-serial-ask';
      const head = document.createElement('p');
      head.className = 'gs-serial-ask__head';
      head.textContent = ask.intro;
      const out = document.createElement('p');
      out.className = 'gs-serial-ask__out';
      out.setAttribute('role', 'status');
      const picked = [];
      const verdict = () => {
        const got = picked.filter(Boolean).map(ids => ids.join(','));
        const same = got.length && got.every(g => g === got[0]);
        const ids = same ? got[0].split(',') : [];
        out.textContent = !got.length ? '' : same ? (got.length < ask.qs.length ? 'That points to the ' : (got.length > 1 ? 'Both answers point to the ' : 'That points to the ')) + names(ids) + '.' +
          (ids.length > 1 ? ' Those two look alike: text us a photo of the bow at 270-318-2020 to be sure.' : (ask.after?.[ids[0]] || ''))
          : 'Those answers point to different bows. Text us a photo of the bow and its label at 270-318-2020 and we\'ll tell you.';
        // the bow they point to becomes the main button, worded as the sure answer; the others go back to "Is it the …?"
        div.querySelectorAll('a[data-gs-oneida]').forEach(a => {
          const main = ids.length === 1 && a.dataset.gsOneida === slug(ids[0]);
          a.dataset.gsAsk ??= a.textContent;
          a.classList.toggle('gs-btn--ghost', !main);
          a.textContent = main ? 'Show ' + names(ids) + ' sizes and strings →' : a.dataset.gsAsk;
        });
      };
      box.append(head);
      ask.qs.forEach((item, i) => {
        const q = document.createElement('div');
        q.className = 'gs-serial-ask__q';
        const p = document.createElement('p');
        p.textContent = item.q;
        p.id = 'gs-ask-' + (++askIds);
        const row = document.createElement('div');
        row.className = 'gs-chips';
        row.setAttribute('role', 'group');
        row.setAttribute('aria-labelledby', p.id); // a screen reader hears the question with its buttons
        item.a.forEach(([text, ids]) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'gs-chip';
          b.textContent = text;
          b.setAttribute('aria-pressed', 'false');
          b.addEventListener('click', () => {
            const on = b.getAttribute('aria-pressed') !== 'true'; // a second tap takes the pick back
            row.querySelectorAll('button').forEach(x => x.setAttribute('aria-pressed', String(on && x === b)));
            picked[i] = on ? ids : null;
            verdict();
          });
          row.append(b);
        });
        q.append(p, row);
        box.append(q);
      });
      box.append(out);
      return box;
    };
    const render = (raw, sent) => {
      const r = decode(raw);
      if (!r) return;
      const pill = { decoded: ['ok', 'From Oneida\'s chart'], family: ['neutral', 'Year only'], years: ['neutral', 'Years only'], model: ['neutral', 'Model only'], check: ['warn', 'Check the number'], none: ['neutral', 'Not in our records'] }[r.conf];
      const div = document.createElement('div');
      div.className = 'gs-step gs-fade';
      div.innerHTML = '<div class="gs-answer-title"><span class="gs-eyebrow"></span><span class="gs-pill"></span></div>' +
        '<h3 style="font-size:1.8rem"></h3><p class="gs-mono" style="font-size:1.15rem"></p><p></p>';
      div.querySelector('.gs-eyebrow').textContent = String(raw).toUpperCase();
      div.querySelector('.gs-pill').classList.add('gs-pill--' + pill[0]);
      div.querySelector('.gs-pill').textContent = pill[1];
      div.querySelector('h3').textContent = r.conf === 'check' ? 'We can\'t read this number' : r.title;
      div.querySelectorAll('p')[0].textContent = r.year ? 'Built ' + r.year : '';
      div.querySelectorAll('p')[1].textContent = r.why;
      // labeled notes that aren't Oneida's chart: our shop's look-alike tip, what collectors say about odd labels
      (r.notes || []).forEach(n => {
        const p = document.createElement('p');
        p.className = 'gs-serial-note';
        const b = document.createElement('b');
        b.textContent = n.label + ':';
        p.append(b, ' ' + n.text);
        div.append(p);
      });
      if (r.ask) div.append(askBox(r.ask, div));
      const unsure = r.links.length > 1 || r.conf === 'check';
      r.links.slice(0, 8).forEach(id => {
        const a = document.createElement('a');
        a.className = 'gs-btn' + (unsure ? ' gs-btn--ghost' : '');
        a.style.marginRight = '8px';
        a.href = '/pages/oneida/' + slug(id);
        a.dataset.gsOneida = slug(id); // remembered as their bow when tapped
        a.textContent = (unsure ? 'Is it the ' : 'Show ') + modelName(id) + (unsure ? '? →' : ' sizes and strings →');
        div.append(a);
      });
      if (r.cpa) div.append(cpaChart(r.cpa));
      if (r.conf !== 'decoded' && contact) div.append(contact.content.cloneNode(true));
      const clean = String(raw).trim().toUpperCase().slice(0, 24);
      // what the reader said, as the contact email gives it: "Year only · Phantom or Phantom II · built 1992"
      const said = [pill[1], r.conf === 'check' ? 'We can\'t read this number' : r.title, r.year ? 'built ' + r.year : '']
        .filter((t, i, a) => t && a.indexOf(t) === i).join(' · ');
      if (tell && !sent && TELL.includes(r.conf) && !EXAMPLES.has(flatSerial(clean))) div.append(tellBox(clean, said));
      const link = '/pages/find-my-bow?serial=' + encodeURIComponent(clean) + '#serial';
      div.append(shareButton(link, 'Oneida serial ' + clean + ': ' + (r.conf === 'check' ? 'check the number' : r.title + (r.year ? ', built ' + r.year : ''))));
      sout.replaceChildren(div);
      history.replaceState(null, '', link);
    };
    sform?.addEventListener('submit', e => { e.preventDefault(); render(sform.serial.value); });
    // Arriving from the home page with ?serial=AEJ1111 (or ?bow=G-Rex&number=1285): fill in and show the answer
    const q = new URLSearchParams(location.search);
    // Back from "Know your bow? Tell us": Shopify adds ?contact_posted=true after the contact form sends. Say thanks
    // (and don't offer the form again for that answer); render() takes the flag back off the address.
    const posted = q.get('contact_posted') === 'true' && !!tell && !!sout;
    if (q.get('serial') && sform) { show('serial'); sform.serial.value = q.get('serial').slice(0, 24); render(sform.serial.value, posted); }
    else if (posted) { show('serial'); history.replaceState(null, '', location.pathname + '#serial'); }
    if (posted) {
      const p = document.createElement('p');
      p.className = 'gs-callout gs-tell-thanks';
      p.setAttribute('role', 'status');
      p.innerHTML = '<span><b>Thanks, we got it.</b> What you told us about your Oneida helps us fill in Oneida\'s missing serial records.</span>';
      sout.prepend(p);
    }
    root.querySelectorAll('[data-gs-serial-ex]').forEach(b => b.addEventListener('click', () => { sform.serial.value = b.dataset.gsSerialEx; render(b.dataset.gsSerialEx); }));
  });

  /* Returns { title, year, why, links: [model ids], conf, notes } where conf is:
     'decoded' (the chart settles it), 'family' (the year is read; the chart allows more than one model),
     'years' (only a span of years), 'model' (the model letters read, no year: the hand-lettered labels),
     'check' (doesn't read right: check the number) or 'none' (not in our records).
     notes: [{ label, text }], said under the answer with their label (LOOKALIKE, HANDWRITTEN above); may be missing. */
  function decode(raw) {
    const r = read(raw);
    if (!r) return r;
    if (r.lookalike || r.links.some(id => LOOKALIKE_IDS.includes(id))) r.notes = [...(r.notes || []), LOOKALIKE];
    delete r.lookalike;
    return r;
  }
  // a hand-lettered label: code "LM", "AC" or "TX" (written in), then the typed letter (Q or R) and number
  function handLettered(code, letter, digits) {
    const ids = code === 'LM' ? ['liteforce'] : code === 'AC' ? ['aeroforce'] : ['tomcatexp', 'tomcat3exp'];
    const typed = digits ? ', like ' + letter + digits : '';
    let why = code === 'LM' ? '"LM" is Oneida\'s code for the Lite Force / LF Magnum.'
      : code === 'AC' ? '"A" is Oneida\'s code for the AeroForce, and "C" after it means camo on Oneida\'s chart.'
        : '"T" is Oneida\'s code for the Tomcat family, and we read a hand-written "Tx" as a Tomcat EXP or a T-III EXP (the youth Tomcats). The label doesn\'t say which one, and neither is on Oneida\'s serial chart.';
    why += ' Some labels have the model letters written in by hand next to a typed letter and number' + typed + '.';
    if (letter === 'R') why += ' "R" isn\'t one of Oneida\'s year letters (their table ends at Q, for 2000), so we don\'t read a year from it.';
    else if (code === 'LM') why += ' "Q" is the year letter for 2000 on Oneida\'s chart, but the chart lists the Lite Force for 1997 only (our Lite Force chart says 1997 to 1999), so we don\'t read a year from it.';
    else why += ' "Q" is the year letter for 2000 on Oneida\'s chart, but these youth Tomcats aren\'t on the chart, so we don\'t read a year from it.';
    return { title: names(ids), year: '', conf: 'model', links: ids, notes: [HANDWRITTEN, HAND_BE], why: why + PHOTO };
  }
  /* "A" + numbers: Oneida's chart reads it as June 1986 – March 1987 (the pylon H-series years). But C.P. Oneida (2001 on)
     engraved "A" and the bow's C.P.A. number on some risers: a bow in Joey's shop reads "A 0193" on the riser and "C.P.A. 0193"
     on its limb label (Joey, Oct 26 2026: "the engraved number starts with an a and the tag on the limb is the same number
     with a cp in front"; #157 "yes change it"). So the chart's answer stays, and the C.P.A. reading goes under its own label
     with the C.P. chart rows the number sits on. One bow proves it can be; it isn't on any Oneida paper. */
  function aNumber(digits) {
    const p = cpaPlace(digits);
    return { title: 'Built 1986–87, or a C.P. Oneida bow', year: '', conf: 'years', links: [...new Set(p.rows.flatMap(r => r[5]))],
      lookalike: true, cpa: p.rows.map(r => CPA.indexOf(r)),
      why: 'On Oneida\'s chart, an "A" followed only by numbers marks bows built from June 1986 to March 1987, which all had the pylon riser. The model isn\'t in the number. (AeroForce serials have two more letters after the A, like AEJ1111.) If your bow doesn\'t have the pylon riser, read the note below.' + PHOTO,
      notes: [{ label: 'From our shop', text: 'On a C.P. Oneida bow (2001 on, no pylon riser) the number engraved on the riser can be an "A" and the bow\'s C.P.A. number, the same number as on its limb label. One in our shop reads "A 0193" on the riser and "C.P.A. 0193" on the label. Read as C.P.A. ' + digits + ': ' + p.said.join(' ') }] };
  }
  function newer(s) {
    const code = s.match(/^(OSR|PHX)(S|M|L|SLF|MLF)?$/);
    if (code) {
      const [, kind, size] = code;
      if (kind === 'PHX') {
        if (size && !SIZE[size]) return { title: 'We can\'t read this number', year: '', conf: 'check', links: ['phoenix'],
          why: '"PHX" is the Phoenix on the newer Oneida labels, but "' + size + '" isn\'t one of its sizes (Short, Medium or Long). Please check the label.' + PHOTO_LABEL };
        return { title: 'Phoenix' + (size ? ', ' + SIZE[size] : ''), year: '', conf: 'model', links: ['phoenix'],
          why: '"PHX" is the Phoenix on the newer Oneida labels' + (size ? ', and "' + size + '" is the ' + SIZE[size] : '') + '. ' + PHOENIX_STRINGS + PHOTO_LABEL };
      }
      if (size === 'SLF' || size === 'MLF') return { title: 'Osprey (before 2017), ' + (size === 'SLF' ? 'Super Short (SLF)' : 'Medium L.F.'), year: '', conf: 'model', links: ['osprey_cp'],
        why: '"OSR" is the Osprey, and ' + size + ' is one of the older Osprey size names (before 2017). Check the string length on your label. ' + OLDER_OSPREY + PHOTO_LABEL };
      return { title: 'Osprey' + (size ? ', ' + SIZE[size] : ''), year: '', conf: 'model', links: ['osprey_new', 'osprey_cp'],
        why: '"OSR" is the Osprey on Oneida\'s labels' + (size ? ', and "' + size + '" is the ' + SIZE[size] : '') + '. The code alone doesn\'t tell an older Osprey from a newer one, so go by the string length and size on your label: ' + NEWER_STRINGS + '. ' + OLDER_OSPREY + PHOTO_LABEL };
    }
    const o = s.match(/^O([SML])\d+$/);
    if (o) return { title: 'Osprey (2017–today), ' + SIZE[o[1]], year: '', conf: 'model', links: ['osprey_new'], notes: [SHOP_O],
      why: 'Oneida\'s serial chart doesn\'t cover the newer Ospreys. Check the string length on your label: ' + NEWER_STRINGS + '. A different length may mean an older Osprey (before 2017).' + PHOTO_LABEL };
    if (/^P([SML]\d*|\d)/.test(s) && !/^P[SML][A-Z]/.test(s)) return { title: 'Not in our records', year: '', conf: 'none', links: [],
      why: 'This doesn\'t fit Oneida\'s Phantom serials: on their chart those are P, then E (not camo) or C (camo), then a year letter, like PCI1111. Newer Oneida labels also have a Model line, like PHX-M for a Phoenix Medium or OSR-L for an Osprey Long. Type that in to see the model and size.' + PHOTO };
    return null;
  }
  function read(raw) {
    let s = String(raw).toUpperCase().trim();
    s = s.replace(/^(SERIAL\s*(NO\.?|NUMBER|#)?|S\/N|SN|NO\.|MODEL)\s*[:#]?\s*/, '').replace(/^#/, '');
    s = s.replace(/[\s\-–—.]/g, '');
    if (!s) return null;
    // a typed Q or R and a number with the model letters written in by hand (LM Q…, AC R…, Tx R…; typed in either order),
    // read only where Oneida's chart can't read it: AC + Q is AeroForce, camo, 2000 on the chart, so it's left to the chart.
    // BE written after the number (Joey's Black Eagle label "Q10769" with BE in blue, Oct 26 2026: "yes" to reading BE
    // either side) is turned round to BE + Q…, which the chart reads (BE = Black Eagle, 2000).
    const be = s.match(/^([QR]\d+)BE$/);
    if (be) s = 'BE' + be[1];
    const se = s.match(/^(Q\d+)SE$/); // SE written after a Q number reads like SE Q… (Oneida's chart: Stealth Eagle), as BE does
    if (se) s = 'SE' + se[1];
    let hw = s.match(/^(LM|AC|TX)([QR])(\d*)$/);
    if (!hw) { const b = s.match(/^([QR])(\d+)(LM|AC|TX)$/); if (b) hw = [b[0], b[3], b[1], b[2]]; }
    if (hw && !(hw[1] === 'AC' && hw[2] === 'Q')) return handLettered(hw[1], hw[2], hw[3]);
    const nl = newer(s);
    if (nl) return nl;
    if (/^Q\d{4,}$/.test(s)) { // a Q and four digits, the shape of the bare-Q labels seen (fewer digits: as before)
      const five = s.length > 5; // five digits or more: our one Q label that high was a Black Eagle
      return { title: five ? 'Black Eagle, Stealth Eagle or Lite Force' : 'Stealth Eagle or Lite Force', year: '', conf: 'model',
        links: five ? ['blackeagle00', 'stealth', 'liteforce'] : ['stealth', 'liteforce'], notes: five ? [Q_LABELS] : [Q_LABELS, Q_TICKS], ask: ASK_Q(five),
        why: 'A typed "Q" and numbers on their own aren\'t on Oneida\'s serial chart (Q is its year letter for 2000, and the model letters are missing), so this comes from the labels we\'ve seen, not from Oneida. Typed Q labels turn up on bows from about 1999 too, so we don\'t give a year.' +
          (five ? ' The Stealth Eagle and Lite Force labels we\'ve seen had four digits after the Q; the one Black Eagle had five.' : '') + PHOTO_LABEL };
    }
    if (/^[QR]\d+$/.test(s)) return { title: 'Not in our records', year: '', conf: 'none', links: [],
      why: (s[0] === 'R' ? 'An' : 'A') + ' "' + s[0] + '" and numbers on their own aren\'t on Oneida\'s chart. Some labels have the model letters written in by hand next to a typed number like this (LM, AC, BE or Tx, for example). If yours does, type them in front, like LM Q1234.' + PHOTO };
    if (/^\d+$/.test(s)) return { title: 'Numbers only: the model isn\'t in it', year: '', conf: 'none', links: [],
      why: 'On Oneida\'s chart, serial numbers with no letters were used from 1982 to 1985, and they don\'t show the model. Those were the H500 and H250, with the pylon (H-style) riser: if your bow doesn\'t have that riser, it isn\'t one of those early numbers, and we don\'t have records for numbers like it yet. If this is the number under the grip of a G-Rex, Gnat or Pro2G, use the G-String bow tab.' + PHOTO };
    if (/^AO\d+$/.test(s)) return { title: 'We can\'t read this number', year: '', conf: 'check', links: [], lookalike: true,
      why: 'If that "O" is a zero, this is an "A" followed only by numbers, which Oneida\'s chart says means built June 1986 to March 1987 (the model isn\'t in the number). On a C.P. Oneida bow (2001 on, no pylon riser) it can be the bow\'s C.P.A. number engraved on the riser instead. AeroForce serials have two letters after the A, like AEJ1111. Please check the number.' + PHOTO };
    if (/^A\d+$/.test(s)) return aNumber(s.slice(1));
    if (/^CPA\d*$/.test(s)) return decodeCPA(s);
    if (/^SELXV\d*$/.test(s)) return { title: 'Screaming Eagle 689 LXV', year: '1989', conf: 'decoded', links: ['screaming'],
      why: 'Oneida\'s serial chart lists "SELXV" as the Screaming Eagle 689 LXV (65% let-off), built in 1989.' };
    if (/^BE[A-Z]\d*$/.test(s) && !/^BEQ/.test(s)) return { title: 'We can\'t read this number', year: '', conf: 'check', links: ['blackeagle00'],
      why: '"BE" is Oneida\'s code for the Black Eagle, which their chart lists for 2000, but the letter after it isn\'t 2000\'s year letter (Q).' + (s[2] === 'P' ? ' P is the chart\'s letter for 1999, but the chart doesn\'t list the Black Eagle that year.' : '') + ' Please check the number.' + PHOTO, notes: [BE_1999] };
    if (/^BEQ?\d*$/.test(s)) return { title: 'Black Eagle (2000)', year: '2000', conf: 'decoded', links: ['blackeagle00'], notes: [BE_1999],
      why: 'Oneida\'s serial chart lists "BE" as the Black Eagle, built in 2000.' };
    if (/^SEQ\d*$/.test(s)) return { title: 'Stealth Eagle', year: '', conf: 'decoded', links: ['stealth'],
      why: 'Oneida\'s serial chart lists "SEQ" as the Stealth Eagle (1998–99 on that chart). Q is the year letter for 2000, so we don\'t read a year from it.' };
    // model code, finish letter (not for H5, H2, LM), year letter, then the bow's own digits. H5/H2 serials from
    // before 1988 have no year letter; LM always has one. The digits can be left off (Joey typed "kcg", Oct 19 2026): the
    // letters alone give the model and year.
    if ((/^(H5|H2)/.test(s) && !/^(H5|H2)[A-Z]?\d*$/.test(s)) || (/^LM/.test(s) && !/^LM[A-Z]?\d*$/.test(s))) return { title: 'We can\'t read this number', year: '', conf: 'check', links: [],
      why: 'That isn\'t the shape of an Oneida serial on their chart (like H5D1111). Please check the number.' + PHOTO };
    const m = s.match(/^(H5|H2|LM|[AKPST])([A-Z0-9])?([A-Z0-9])?(\d*)$/);
    const code = m && m[1];
    // letters only, but not enough of them to reach the year letter ("K", "KC", "H5", "LM"): ask for the rest
    if (m && SERIAL.codes[code] && !m[4] && !((code === 'H5' || code === 'H2' || code === 'LM') ? m[2] : m[3])) {
      const ids = SERIAL.codes[code].filter((id, i, a) => a.indexOf(id) === i);
      return { title: names(ids), year: '', conf: 'check', links: ids.length === 1 ? ids : [],
        why: '"' + code + '" is Oneida\'s code for the ' + names(ids) + '. Type the letters after it too: ' +
          (code === 'H5' || code === 'H2' || code === 'LM' ? 'the next letter is' : 'the 3rd letter is') +
          ' the year letter. You don\'t need the numbers after the letters to tell the model and year.' };
    }
    if (!m || !SERIAL.codes[code]) {
      return { title: 'Not in our records', year: '', conf: 'none', links: [],
        why: 'That doesn\'t match any serial on Oneida\'s chart. Type just the letters and numbers, from the label on a limb or from under the grip. We don\'t have records yet for every Oneida serial, including bows built from 2010 on. If your bow still has its label, send a photo of that too.' + PHOTO };
    }
    const noFinish = code === 'H5' || code === 'H2' || code === 'LM';
    const fin = noFinish ? '' : m[2];
    // H5/H2 serials from before 1988 have no year letter: the digits start right after the code
    const yc = noFinish ? (m[2] && /\d/.test(m[2]) && code !== 'LM' ? '' : m[2]) : m[3];
    const candidates = SERIAL.codes[code];
    let why = '"' + code + '" is Oneida\'s code for the ' + names(candidates.filter((id, i, a) => a.indexOf(id) === i)) + '.';
    if (code === 'T') why += ' (The youth Tomcat EXP and T-III EXP aren\'t on Oneida\'s serial chart.)';
    // Oneida's printed examples explain the 2nd letter only for these (AEJ1111 = not camo, PCI1111 = camo)
    if (code === 'A' || code === 'P') {
      if (fin === 'E') why += ' The "E" after it means not camo on Oneida\'s chart.';
      if (fin === 'C') why += ' The "C" after it means camo on Oneida\'s chart.';
    }
    if (!noFinish && fin && fin !== 'E' && fin !== 'C') {
      return { title: names(candidates), year: '', conf: 'check', links: candidates.length === 1 ? candidates : [],
        why: why + ' The 2nd character should be E or C, and the 3rd a year letter, but we see "' + fin + '". Please check the number.' + PHOTO };
    }
    if (code === 'S' && (fin === 'E' || fin === 'C') && yc && /\d/.test(yc)) return { title: 'Screaming Eagle', year: '', conf: 'decoded', links: ['screaming'],
      why: why + ' This serial has no year letter. Oneida added year letters in 1988, and the Screaming Eagle was first built in 1987.' };
    if (!yc || /\d/.test(yc)) {
      // no year letter: H500/H250 numbers from before 1988, or a 1/0 typed where a letter goes
      if (noFinish && !yc && code !== 'LM') return { title: names(candidates), year: '', conf: 'decoded', links: candidates,
        why: why + ' This serial has no year letter, so it doesn\'t show the year (Oneida added year letters in 1988).' };
      const hint = yc === '1' ? 'I' : yc === '0' ? 'O' : '';
      const hy = hint && yearOf(hint);
      const fits = hy && candidates.filter(id => inYear(id, hy));
      return { title: names(candidates), year: '', conf: 'check', links: candidates.length === 1 ? candidates : [],
        why: why + ' The 3rd character should be a year letter, but we see ' + (yc ? 'the number ' + yc : 'none') + '.' +
          (fits && fits.length ? ' If it\'s the letter ' + hint + ', that\'s ' + hy + ' on Oneida\'s chart, when they built the ' + names(fits) + '.' : '') + ' Please check the number.' + PHOTO };
    }
    const y = yearOf(yc);
    if (!y) return { title: names(candidates), year: '', conf: 'check', links: candidates.length === 1 ? candidates : [],
      why: why + ' "' + yc + '" isn\'t one of Oneida\'s year letters. Please check the number.' + PHOTO };
    const fits = candidates.filter(id => inYear(id, y));
    if (!fits.length) return { title: names(candidates), year: '', conf: 'check', links: [],
      why: why + ' "' + yc + '" is Oneida\'s year letter for ' + y + ', but their chart doesn\'t list a ' + names(candidates) + ' built in ' + y + '. Please check the number.' + PHOTO };
    why += ' "' + yc + '" is the year letter for ' + y + '.';
    if (!m[4]) why += ' You don\'t need the numbers after the letters to tell the model and year.';
    if (fits.length === 1) return { title: names(fits), year: String(y), conf: 'decoded', links: fits, why };
    return { title: names(fits), year: String(y), conf: 'family', links: fits,
      why: why + ' Oneida built more than one model with this code that year, and the serial doesn\'t say which. Check the label on your bow, or text us a photo at 270-318-2020.' };
  }

  /* ---------- the hidden bow key (_spec) can take a suffix from a choice: Dead Wake Archery cable orders ask which outer
     limbs the bow has, and "machined" or "standard" picks the yoke length on the build sheet (dwai-kraken-machined).
     "Not sure" leaves the plain key, whose build sheet entry shows both yokes. ---------- */
  function specSuffix(root) {
    const pick = root.querySelector('[data-gs-spec-pick]');
    const opt = pick?.selectedOptions[0];
    root.querySelectorAll('[data-gs-spec]').forEach(inp => {
      // the base comes from the page (data-gs-spec-base), never from the value, which the browser can put back with a suffix
      if (!('base' in inp.dataset)) inp.dataset.base = inp.dataset.gsSpecBase ?? inp.value;
      if ('gsSpecFixed' in inp.dataset) { inp.value = inp.dataset.base; return; } // a model's timing key: never a suffix
      const key = opt?.dataset.specKey; // a whole bow key: the exact model picked (RPM Nitro XX XL or XX Mag)
      const suf = opt?.dataset.spec || '';
      inp.value = key || (inp.dataset.base && suf ? inp.dataset.base + '-' + suf : inp.dataset.base);
    });
    // the exact model picked also names the bow on the order ("Bow Model"), so the two never disagree
    root.querySelectorAll('[data-gs-autofill="bow"]').forEach(inp => {
      if (!('pageBow' in inp.dataset)) inp.dataset.pageBow = inp.value;
      inp.value = opt?.dataset.bow || inp.dataset.pageBow;
    });
  }
  document.addEventListener('change', e => {
    if (!e.target.matches('[data-gs-spec-pick]')) return;
    const root = e.target.closest('[data-gs-buy]');
    if (root) specSuffix(root);
  });

  /* ---------- "I want custom lengths" on string and cable orders (snippets/gs-custom-lengths.liquid). Off: nothing is
     sent and we build to spec. On: the boxes and the "can't be returned" tick count, and at least one length is needed.
     No spec on file (data-gs-nospec): no toggle, every box is required, and the hidden _no_spec line flags the order. */
  // function declarations (hoisted): the size picker above can call setBowContext → setNospec while the page loads
  function customCheck(box) {
    const nospec = box.hasAttribute('data-gs-nospec');
    const on = nospec || !!box.querySelector('[data-gs-custom-on]')?.checked;
    const lens = [...box.querySelectorAll('[data-gs-custom-len]')].filter(i => !i.disabled); // the builder turns other models' boxes off
    const any = lens.some(i => /[0-9]/.test(i.value));
    lens.forEach((i, n) => {
      let m = '';
      if (nospec) { if (!/[0-9]/.test(i.value)) m = 'Please type your ' + (i.dataset.gsCustomName || '') + ' length, or text us a photo at 270-318-2020.'; }
      else if (on && !any && n === 0) m = 'Type at least one length, or untick "' + (box.querySelector('[data-gs-custom-on]')?.closest('label').textContent.trim() || 'I want custom lengths') + '".';
      i.setCustomValidity(m);
    });
  }
  function setNospec(box, nospec) {
    // same mode as before (picking another size, or the same one again): leave what the customer typed and ticked alone
    if (box.hasAttribute('data-gs-nospec') === nospec) { customCheck(box); return; }
    box.toggleAttribute('data-gs-nospec', nospec);
    box.classList.toggle('is-nospec', nospec);
    const tog = box.querySelector('[data-gs-custom-on]');
    tog.checked = false; // no spec: there's no toggle; back to spec: starts closed, like a fresh card
    tog.closest('label').hidden = nospec;
    box.querySelector('[data-gs-nospec-lead]').hidden = !nospec;
    box.querySelector('[data-gs-custom-hint]').hidden = nospec;
    const body = box.querySelector('[data-gs-custom-body]');
    body.hidden = !nospec;
    body.querySelectorAll('input').forEach(i => { i.disabled = !nospec; });
    body.querySelectorAll('[data-gs-custom-len]').forEach(i => {
      i.required = nospec;
      i.placeholder = nospec ? i.dataset.gsPlaceholderNospec : i.dataset.gsPlaceholder;
    });
    body.querySelectorAll('[data-gs-custom-ack]').forEach(i => { i.checked = false; });
    customCheck(box);
  }
  window.GSCustomCheck = customCheck;
  window.GSSetNospec = setNospec; // the string listing's brand pick (assets/gs-choices.js)
  // a tick the browser put back (reload, back button) opens its boxes again
  const customSync = () => document.querySelectorAll('[data-gs-custom]:not([data-gs-nospec])').forEach(box => {
    const t = box.querySelector('[data-gs-custom-on]');
    if (t?.checked && box.querySelector('[data-gs-custom-body]').hidden) t.dispatchEvent(new Event('change', { bubbles: true }));
  });
  document.querySelectorAll('[data-gs-custom]').forEach(customCheck);
  customSync();
  window.addEventListener('pageshow', customSync);
  document.addEventListener('change', e => {
    const on = e.target.closest('[data-gs-custom-on]');
    if (!on) return;
    const box = on.closest('[data-gs-custom]');
    if (box.hasAttribute('data-gs-nospec')) return;
    const body = box.querySelector('[data-gs-custom-body]');
    body.hidden = !on.checked;
    // the tip-to-tip box only counts with no spec on file (gs-choices.js turns it on for our bows)
    body.querySelectorAll('input:not([data-gs-nospec-flag]):not([data-gs-tip-input])').forEach(i => { i.disabled = !on.checked; });
    if (!on.checked) body.querySelectorAll('[data-gs-custom-ack]').forEach(i => { i.checked = false; });
    customCheck(box);
    if (on.checked) body.querySelector('input:not([type="hidden"])')?.focus();
  });
  document.addEventListener('input', e => { const box = e.target.closest('[data-gs-custom]'); if (box) customCheck(box); });

  /* ---------- "Fit the center serving to my nock" on string orders (snippets/gs-nock.liquid; Joey, Oct 25 2026, #147:
     "a box to check if you want to fit a particular nock size and the customer can type the nock they use"). Not ticked:
     the box is off and nothing is sent. Ticked: "Your nock" opens and needs real text (not just spaces), and goes on the
     order as "Nock to fit". The buy cards' Add to cart says "Please fill in your nock…"; the product page's own form
     stops on the message below. ---------- */
  const NOCK_MSG = 'Type your nock (brand and size), or untick "Fit the center serving to my nock".';
  function nockCheck(box) {
    const on = !!box.querySelector('[data-gs-nock-on]')?.checked;
    const inp = box.querySelector('[data-gs-nock-text]');
    if (!inp) return;
    box.querySelector('[data-gs-nock-body]').hidden = !on;
    inp.disabled = !on; // a disabled box isn't sent with the order
    inp.required = on;
    inp.setCustomValidity(on && !inp.value.trim() ? NOCK_MSG : '');
  }
  // a tick the browser put back (reload, back button) opens its box again
  const nockSync = () => document.querySelectorAll('[data-gs-nock]').forEach(nockCheck);
  nockSync();
  window.addEventListener('pageshow', nockSync);
  document.addEventListener('change', e => {
    const on = e.target.closest('[data-gs-nock-on]');
    if (on) {
      const box = on.closest('[data-gs-nock]');
      nockCheck(box);
      if (on.checked) box.querySelector('[data-gs-nock-text]')?.focus();
      return;
    }
    const t = e.target.closest('[data-gs-nock-text]');
    if (t) { t.value = t.value.trim(); nockCheck(t.closest('[data-gs-nock]')); }
  });
  document.addEventListener('input', e => { if (e.target.closest('[data-gs-nock-text]')) nockCheck(e.target.closest('[data-gs-nock]')); });

  /* ---------- Joey's cable color layers (snippets/gs-cable-stack.liquid): picking a cable's color swaps just that cable's
     layer. The color comes from the same product's picker (a parts card, the product page or a quick-add window): its
     "<Power/Yoke/Timing> Cable Color" option, or a full set's "properties[... Cable Color]" choice. Nothing picked (or
     back to "Choose…") shows the cable's first color again, with no color named. ---------- */
  function cstackUpdate() {
    document.querySelectorAll('[data-gs-cstack] img[data-gs-cstack-option]').forEach(img => {
      const name = img.dataset.gsCstackOption;
      const scope = img.closest('[data-gs-buy], [data-product-grid-content], dialog') || document;
      const pick = scope.querySelector('input[data-option-name="' + name + '"]:checked')                          // product page variant buttons
        || scope.querySelector('select[name="options[' + name + ']"]')                                            // product page variant dropdown
        || scope.querySelector('select[aria-label="' + name + '"]')                                               // card variant select
        || [...scope.querySelectorAll('fieldset[data-gs-option]')].find(f => (f.querySelector('legend')?.textContent || '').trim().startsWith(name))?.querySelector('input:checked')
        || scope.querySelector('select[name="properties[' + name + ']"], input[name="properties[' + name + ']"]:checked'); // a full set's color choices
      const val = pick ? pick.value : '';
      const urls = Object.fromEntries((img.dataset.gsCstackUrls || '').split('|').filter(Boolean).map(kv => [kv.slice(0, kv.indexOf('=')), kv.slice(kv.indexOf('=') + 1).split(',')]));
      const slug = val.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const known = !!urls[slug];
      const [small, big] = urls[known ? slug : img.dataset.gsCstackStart] || [];
      if (!big) return;
      if (img.getAttribute('src') !== big) { img.srcset = small + ' 600w, ' + big + ' 1200w'; img.src = big; }
      img.alt = img.dataset.gsCstackLabel + (known ? ', ' + val : '');
    });
  }
  document.addEventListener('change', () => { if (document.querySelector('[data-gs-cstack]')) cstackUpdate(); });
  // The Oneida listings' product pages show both sets: "Without pylons" / "Pylon-style" switches the picture and its model list.
  // A part's "Where it goes" drawings (blocks/gs-product-fit) use the same switch; every switch on the page follows the
  // one clicked, so the customer picks their kind of bow once.
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-gs-cstack-show]');
    if (!btn) return;
    const kind = btn.dataset.gsCstackShow;
    document.querySelectorAll('[data-gs-cstack-wrap]').forEach(wrap => {
      if (!wrap.querySelector(`[data-gs-cstack-show="${kind}"]`)) return;
      wrap.querySelectorAll('[data-gs-cstack-show]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.gsCstackShow === kind)));
      wrap.querySelectorAll('[data-gs-cstack-kind]').forEach(s => { s.hidden = s.dataset.gsCstackKind !== kind; });
      wrap.querySelectorAll('[data-gs-cstack-note]').forEach(n => { n.hidden = n.dataset.gsCstackNote !== kind; });
    });
  });
  document.addEventListener('DOMContentLoaded', cstackUpdate);
  if (document.readyState !== 'loading') cstackUpdate();
  window.addEventListener('pageshow', cstackUpdate);

  /* ---------- Parts cards on bow pages: options, price, photo, add to cart ---------- */
  const money = cents => (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  function setBowContext(root, ctx) {
    root.querySelectorAll('[data-gs-buy]').forEach(card => {
      card.querySelectorAll('[data-gs-autofill]').forEach(inp => {
        delete inp.dataset.pageBow; // specSuffix takes the new page value as the bow again
        inp.value = inp.dataset.gsAutofill.split(',').map(k => {
          if (k === 'string_length') return ctx.string_length ? ctx.string_length + '″' : '';
          return ctx[k] || '';
        }).filter(Boolean).join(' · ');
      });
      // an Oneida timing cable keeps its model's key: the riser sets its length, not the size (data-gs-spec-fixed)
      card.querySelectorAll('[data-gs-spec]:not([data-gs-spec-fixed])').forEach(inp => { inp.dataset.base = ctx.spec || ''; specSuffix(card); });
      // A size with no length on file (brand model pages): the string or cable card asks for the customer's own numbers
      card.querySelectorAll('[data-gs-custom]').forEach(box => {
        if (card.dataset.role === 'string_cables') return; // String Cables carry their own public lengths: never "not on file"
        const key = card.dataset.role === 'string' ? 'nospec_string' : 'nospec_cables';
        if (key in ctx) setNospec(box, !!ctx[key]);
      });
      if (card.dataset.needsSize === 'true') card.dataset.sizeReady = 'true';
    });
    const hint = root.querySelector('[data-gs-parts-hint]');
    if (hint && ctx.bow) hint.textContent = 'Built for your ' + ctx.bow + (ctx.string_length ? ' (' + ctx.string_length + '″ string)' : '') + '. Pick colors and add to cart.';
  }
  // a choice the page starts with (or the browser puts back) already counts toward the bow key
  document.querySelectorAll('[data-gs-buy]').forEach(card => { if (card.querySelector('[data-gs-spec-pick]')) specSuffix(card); });
  document.querySelectorAll('[data-gs-buy]').forEach(card => {
    const form = card.querySelector('[data-gs-buy-form]');
    const variants = JSON.parse(card.querySelector('[data-gs-variants]').textContent);
    const msg = card.querySelector('[data-gs-buy-msg]');
    const addLabel = form.querySelector('.gs-buy-add')?.textContent.trim() || 'Add to cart'; // "+ Add" on the small "You may also need" ones
    const chosen = () => [...form.querySelectorAll('[data-gs-option]')].map(fs => {
      const el = fs.querySelector('input:checked, select');
      return el ? el.value : null;
    });
    // Liquid lists only a product's first 250 variants (the G-Rex power limbs have 290): for the rest, ask Shopify for the
    // variant of the picked option values. Never fall back to another variant: no match means it can't be added.
    const more = card.hasAttribute('data-gs-more-variants');
    const found = {};
    const lookup = async opts => {
      const ids = [...form.querySelectorAll('[data-gs-option]')].map(fs => {
        const el = fs.querySelector('input:checked, select');
        return (el && (el.tagName === 'SELECT' ? el.selectedOptions[0] : el))?.dataset.ovid;
      });
      if (ids.some(x => !x)) return null;
      const key = ids.join(',');
      if (!(key in found)) {
        // only a real answer is remembered; a network hiccup or a slow reply (8 s) can be tried again
        try {
          const res = await fetch(card.dataset.gsUrl + '?option_values=' + key + '&section_id=gs-variant-json', { signal: AbortSignal.timeout(8000) });
          if (!res.ok) return 'error';
          const m = (await res.text()).match(/<script type="application\/json" data-gs-variant>([\s\S]*?)<\/script>/);
          if (!m) return 'error';
          const v = JSON.parse(m[1]);
          found[key] = v && v.options && v.options.every((o, i) => opts[i] == null || o === opts[i]) ? v : null;
        } catch (e) { return 'error'; }
      }
      return found[key];
    };
    let seq = 0;
    const update = async () => {
      const opts = chosen();
      const mine = ++seq;
      const btn0 = form.querySelector('.gs-buy-add');
      let v = variants.find(x => x.options.every((o, i) => opts[i] == null || o === opts[i]));
      let failed = false;
      if (!v && more) {
        btn0.disabled = true;
        form.querySelector('input[name="id"]').value = ''; // nothing can be added while we look it up
        v = await lookup(opts);
        if (mine !== seq) return; // a newer pick is on its way
        if (v === 'error') { v = null; failed = true; }
      }
      form.querySelectorAll('[data-gs-option]').forEach((fs, i) => { const n = fs.querySelector('[data-gs-swatch-name]'); if (n) n.textContent = opts[i]; });
      if (!v) {
        form.querySelector('input[name="id"]').value = '';
        btn0.disabled = true;
        btn0.textContent = failed ? addLabel : 'Not available';
        msg.textContent = failed ? 'Couldn\'t load that choice. Pick it again, or reload the page.' : '';
        finish(opts);
        return;
      }
      if (msg.textContent.startsWith('Couldn\'t load')) msg.textContent = '';
      form.querySelector('input[name="id"]').value = v.id;
      card.querySelector('[data-gs-buy-price]').textContent = money(v.price);
      const btn = form.querySelector('.gs-buy-add');
      btn.disabled = !v.available;
      btn.textContent = v.available ? addLabel : 'Sold out';
      const img = card.querySelector('[data-gs-buy-img]');
      if (img && v.featured_image) { img.src = v.featured_image.src + (v.featured_image.src.includes('?') ? '&' : '?') + 'width=520'; img.removeAttribute('srcset'); }
      finish(opts);
    };
    // no finish on a pattern or on unpainted limbs (the shop's rule): the Finish choice turns off and isn't sent
    const finBox = form.querySelector('[data-gs-finish]');
    const nofin = new Set((finBox?.dataset.gsNofin || '').split('|').filter(Boolean));
    const handle = v => String(v || '').toLowerCase().replace(/['’]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    const finish = opts => {
      if (!finBox) return;
      const none = opts.some(o => o != null && nofin.has(handle(o)));
      if ('gsAutoSolid' in finBox.dataset) { // follows the paint: Cerakote on a solid, film dip on a pattern, none on raw
        const raw = opts.some(o => o != null && handle(o).includes('no-paint'));
        const v = none ? finBox.dataset.gsAutoPattern : finBox.dataset.gsAutoSolid;
        finBox.querySelectorAll('input').forEach(i => { i.value = v; i.disabled = raw; });
        finBox.querySelector('[data-gs-auto-text]').textContent = v;
        finBox.hidden = raw;
        return;
      }
      const ano = opts.some(o => o != null && /(^|-)anodize$/.test(handle(o)));
      finBox.hidden = none;
      finBox.querySelectorAll('select').forEach(s => { s.disabled = none; anodizeOnly(s, ano && !none); });
    };
    // a color that IS an anodize ("Clear anodize", "Yellow anodize": Joey, Oct 25 2026, "clear is anodize only") locks
    // the Finish choice to Anodize; any other color frees it again
    const anodizeOnly = (s, ano) => {
      const a = [...s.options].find(o => /^anodize/i.test(o.value));
      if (!a) return;
      [...s.options].forEach(o => { if (o.value) o.disabled = ano && o !== a; });
      if (ano) s.value = a.value;
    };
    finish(chosen());
    // String Cables (Joey, Oct 20 2026): offer only this bow's cable systems, show the picked one's length (public) and
    // send it with the order as "Cable length"
    const pubJson = form.querySelector('[data-gs-pubcables]');
    if (pubJson) {
      const list = JSON.parse(pubJson.textContent) || [];
      const sel = form.querySelector('[data-gs-option] select');
      if (sel) {
        [...sel.options].forEach(o => { const ok = list.some(c => c.system === o.value); o.disabled = !ok; o.hidden = !ok; });
        const first = [...sel.options].find(o => !o.disabled);
        if (sel.selectedOptions[0]?.disabled && first) { sel.value = first.value; update(); }
      }
      const pub = () => {
        const c = list.find(x => x.system === chosen()[0]);
        form.querySelector('[data-gs-pubcable-text]').textContent = c ? c.count + ' cables, ' + c.length + '″ each' : 'call or text us for this one';
        form.querySelector('[data-gs-pubcable-len]').value = c ? c.length + '″ each (' + c.count + ' cables)' : '';
      };
      pub();
      form.addEventListener('change', e => { if (e.target.closest('[data-gs-option]')) pub(); });
    }
    form.addEventListener('change', e => { if (e.target.closest('[data-gs-option]')) update(); });
    form.addEventListener('submit', async e => {
      e.preventDefault();
      msg.textContent = '';
      if (card.dataset.needsSize === 'true' && card.dataset.sizeReady !== 'true') {
        msg.textContent = 'Pick your size above first, so we build it to the right length.';
        card.closest('[data-gs-model]')?.querySelector('.gs-chips')?.scrollIntoView({ behavior: smooth(), block: 'center' });
        return;
      }
      if (!form.querySelector('input[name="id"]').value) { msg.textContent = 'That combination isn\'t available online. Call or text us at 270-318-2020.'; return; }
      const custom = [...form.querySelectorAll('[data-gs-custom] input:not(:disabled)')].find(el => !el.checkValidity());
      if (custom) {
        msg.textContent = custom.type === 'checkbox' ? 'Please tick the box to say you understand custom lengths can\'t be returned.' : custom.validationMessage;
        custom.focus(); return;
      }
      const missing = [...form.querySelectorAll('[required]:not(:disabled)')].find(el => (el.type === 'checkbox' ? !el.checked : !el.value.trim()));
      if (missing) { msg.textContent = (missing.tagName === 'INPUT' ? 'Please fill in ' : 'Please choose ') + (missing.closest('label')?.querySelector('.gs-buy-label')?.textContent.toLowerCase() || 'an option') + '.'; missing.focus(); return; }
      const properties = {};
      new FormData(form).forEach((val, key) => { const m = key.match(/^properties\[(.+)\]$/); if (m && String(val).trim()) properties[m[1]] = String(val).trim(); });
      const qtyBox = form.querySelector('[data-gs-qty]'); // a part this bow takes 2 sets of (gstring.sets_per_bow, Oneida model pages)
      const qty = qtyBox ? Number(qtyBox.value) : 1;
      if (!Number.isInteger(qty) || qty < 1 || qty > 10) { msg.textContent = 'Please enter how many sets, 1 to 10.'; qtyBox.focus(); return; }
      const item = { id: Number(form.querySelector('input[name="id"]').value), quantity: qty, properties };
      const btn = form.querySelector('.gs-buy-add');
      btn.disabled = true; btn.textContent = 'Adding…';
      try {
        await addToCart(card, item);
        msg.textContent = qty > 1 ? 'Added ' + qty + ' sets to your cart.' : 'Added to your cart.';
        // bought for this Oneida (or this brand's model): it's their bow (the size stays whatever they picked); on one of
        // our bows' pages, that bow's key ("g-rex-1285")
        const mroot = card.closest('[data-gs-model]');
        if (mroot) bowSet(mroot.dataset.gsModelHandle);
        const broot = card.closest('[data-gs-bow-key]');
        if (broot) bowSet(broot.dataset.gsBowKey);
      } catch (err) {
        msg.textContent = 'Couldn\'t add that: ' + (err.message || 'please try again.');
      } finally {
        btn.disabled = false; btn.textContent = addLabel;
        if (more) update(); // the button follows the current pick (a lookup may still be running)
      }
    });
  });
  async function addToCart(el, item) {
    let Events = null;
    try { Events = await import('@shopify/events'); } catch (e) { /* cart icon just won't update live */ }
    const deferred = Events?.CartLinesUpdateEvent.createPromise();
    if (Events) el.dispatchEvent(new Events.CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: String(item.id), quantity: item.quantity }], promise: deferred.promise }));
    const res = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [item] }) });
    const body = await res.json();
    const cart = await (await fetch('/cart.js')).json();
    if (Events) deferred.resolve({ cart: Events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { didError: !res.ok, items: cart.items, source: 'gs-buy-card', sourceId: 'gs-buy', itemCount: item.quantity } });
    if (!res.ok) throw new Error(body.description || body.message || 'Add to cart failed');
    return cart;
  }

  /* ---------- Aluminum timing wheels on a double-crimp Oneida (Joey, Oct 24 2026: "let a customer know if they choose them
     for any bow that uses the double crimped end timing cable that it will require a timing cable and the existing timing
     cable for thier factory timing wheels wont work"). The wheel set's own required BOW MODEL dropdown is the "which bow"
     question, on the Oneida model pages' cards (gs-buy-card, which starts it on the page's bow) and on its product page
     (gs-product-choices). One of the double-crimp bows (gstring.wheel_upgrade, data/wheel_upgrade.py) shows the notice
     and the timing cable's own Add button (his pick), and turns on the wheel set's _spec so the build sheet prints the
     UPGRADE warning. That _spec is the wheel set's own (data-gs-wheelup-spec): the bow key code (specSuffix above, setSpec
     in gs-choices.js) only looks at [data-gs-spec], so it never wipes it.
     Never a cable length. ---------- */
  const wheelData = box => { try { return JSON.parse(box.querySelector('[data-gs-wheelup-data]').textContent); } catch (e) { return null; } };
  function wheelUp(box) {
    const d = wheelData(box);
    const scope = box.closest('[data-gs-buy], [data-gs-choices]');
    if (!d || !scope) return;
    const pick = [...scope.querySelectorAll('select')].find(s => s.name === 'properties[' + d.option + ']');
    const val = pick ? pick.value : '';
    const on = (d.values || []).includes(val);
    // the page's own bow goes by the page's name ("Screaming Eagle"); any other is the dropdown's, not in capitals ("Tomcat")
    const bow = !on ? '' : val === d.page_value && d.page_bow ? d.page_bow : val.toLowerCase().replace(/(^|\s)\S/g, c => c.toUpperCase());
    const text = on ? d.message.replaceAll('{bow}', bow) : '';
    // the notice is seen and read out from the same line; the same words again aren't written again, so a screen reader
    // doesn't repeat them
    const say = box.querySelector('[data-gs-wheelup-text]');
    if (say.textContent !== text) say.textContent = text;
    box.querySelector('[data-gs-wheelup-box]').hidden = !on;
    box.querySelector('[data-gs-wheelup-spec]').disabled = !on;
    const msg = box.querySelector('[data-gs-wheelup-msg]');
    if (msg && box.dataset.gsWheelupBow !== bow) msg.textContent = ''; // "Added." was for the bow picked before
    box.dataset.gsWheelupBow = bow;
  }
  document.addEventListener('change', e => {
    const color = e.target.closest('[data-gs-wheelup-variant]');
    if (color) { // the button shows the picked color's price
      const box = color.closest('[data-gs-wheelup]');
      const btn = box.querySelector('[data-gs-wheelup-btn]');
      btn.textContent = btn.dataset.gsLabel + ' (' + (color.selectedOptions[0]?.dataset.price || '') + ')';
      box.querySelector('[data-gs-wheelup-msg]').textContent = '';
      return;
    }
    e.target.closest('[data-gs-buy], [data-gs-choices]')?.querySelectorAll('[data-gs-wheelup]').forEach(wheelUp);
  });
  document.addEventListener('click', async e => {
    const btn = e.target.closest('[data-gs-wheelup-btn]');
    if (!btn || btn.disabled) return;
    const box = btn.closest('[data-gs-wheelup]');
    const d = wheelData(box);
    const color = box.querySelector('[data-gs-wheelup-variant]');
    const msg = box.querySelector('[data-gs-wheelup-msg]');
    const bow = box.dataset.gsWheelupBow;
    if (!d || !bow || !color?.value || color.selectedOptions[0]?.disabled) { msg.textContent = 'Pick a timing cable color first.'; color?.focus(); return; }
    const item = { id: Number(color.value), quantity: 1, properties: { 'Bow Model': bow + ' · for our aluminum timing wheels', For: d.cable_for, _spec: d.cable_key } };
    const label = btn.textContent;
    btn.disabled = true; btn.textContent = 'Adding…'; msg.textContent = '';
    try {
      await addToCart(btn, item);
      msg.textContent = 'Added. Your timing cable will be built for these wheels.';
    } catch (err) {
      msg.textContent = 'Couldn\'t add that: ' + (err.message || 'please try again.');
    } finally {
      btn.disabled = false; btn.textContent = label;
    }
  });
  // a bow the browser put back (reload, back button) fires no change
  const wheelUpAll = () => document.querySelectorAll('[data-gs-wheelup]').forEach(wheelUp);
  wheelUpAll();
  window.addEventListener('pageshow', wheelUpAll);

  /* ---------- Product pages: carry bow details from a "what to order" link into the cart ---------- */
  const params = new URLSearchParams(location.search);
  const bow = params.get('bow');
  // Products with G-String choices fill their own boxes from the link (assets/gs-choices.js).
  if (bow && /\/products\//.test(location.pathname) && !document.querySelector('[data-gs-choices]')) {
    const details = [['Bow', bow], ['String length', params.get('string') ? params.get('string') + '″' : null], ['Bow number', params.get('number')]];
    const addTo = f => details.forEach(([k, v]) => {
      if (!v || f.querySelector('input[name="properties[' + k + ']"]')) return;
      const i = document.createElement('input');
      i.type = 'hidden'; i.name = 'properties[' + k + ']'; i.value = v.slice(0, 120);
      f.append(i);
    });
    const run = () => document.querySelectorAll('form[action*="/cart/add"]').forEach(addTo);
    run();
    new MutationObserver(run).observe(document.body, { childList: true, subtree: true });
  }

  // The serial reader for other pages (the "Send us your bow" form): decode('AEJ1111') -> { title, year, conf, links, why,
  // notes }, where links are Oneida model handles (/pages/oneida/<handle>, "_" written as "-") and notes the labeled
  // non-Oneida notes (may be missing)
  window.GSSerial = { decode, name: modelName };
})();
